/**
 * ohr-video-circles-reels.js
 * ---------------------------------------------------------------------
 * HHP Customisation: tap-to-expand reels player for the "video circles"
 * feature (sections/section-video-circles.liquid — standalone section —
 * and the "video_circles" block inside sections/product.liquid both
 * render circles via snippets/video-circle-item.liquid). Approved via
 * mockup, 17 Sep 2026.
 *
 * A circle is only interactive ([data-video-circle], added in
 * video-circle-item.liquid) when it actually has a video — image-only
 * fallback circles and empty placeholders are left exactly as they were
 * and never trigger anything here.
 *
 * One reels overlay is built lazily on first use and reused for the
 * whole page, however many separate video-circles groups it contains.
 * Clicking a circle groups navigation (prev/next + progress dots) to
 * that circle's own closest .video-circles-row, so e.g. a standalone
 * section and a product-page block on the same page stay independent.
 *
 * Rather than moving/cloning DOM nodes, this reads the already-loaded
 * source off the clicked circle's own .video-circle__media element
 * (video.currentSrc, falling back to its first <source>, since
 * Shopify's video_tag filter renders <source> children rather than a
 * src attribute directly on <video>) and feeds that into ONE shared
 * <video> inside the modal — simplest thing that works reliably with
 * however many renditions Shopify's video_tag produces.
 *
 * Loaded via a plain <script src="..." defer> tag from both
 * sections/section-video-circles.liquid and the video_circles block in
 * snippets/product.liquid, so it can be included twice on one page
 * (section + block together); the init guard below makes that safe.
 */
(function () {
  if (window.__ohrVideoCirclesReelsInit) return;
  window.__ohrVideoCirclesReelsInit = true;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var overlay, card, video, captionEl, progressWrap, muteBtn, closeBtn, prevBtn, nextBtn;
    var groupItems = [];
    var currentIndex = 0;
    var originRect = null;
    var isMuted = true;
    var isOpen = false;

    function buildOverlay() {
      overlay = document.createElement('div');
      overlay.className = 'reels-overlay';
      overlay.innerHTML =
        '<div class="reels-card">' +
        '<div class="reels-progress"></div>' +
        '<video playsinline loop></video>' +
        '<div class="reels-card__scrim-top"></div>' +
        '<div class="reels-card__scrim-bottom"></div>' +
        '<button type="button" class="reels-nav reels-nav--prev" aria-label="Previous">' +
        '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M15.4 7.4 14 6l-6 6 6 6 1.4-1.4L11 12z"/></svg>' +
        '</button>' +
        '<button type="button" class="reels-nav reels-nav--next" aria-label="Next">' +
        '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M8.6 16.6 10 18l6-6-6-6-1.4 1.4L13 12z"/></svg>' +
        '</button>' +
        '<p class="reels-caption"></p>' +
        '<button type="button" class="reels-mute" aria-label="Mute or unmute">' +
        '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12A4.5 4.5 0 0 0 14 8v8a4.5 4.5 0 0 0 2.5-4Z"/><path d="M14 3.2v2.1c3.3 1 5.7 4 5.7 7.7s-2.4 6.7-5.7 7.7v2.1c4.4-1 7.7-5 7.7-9.8S18.4 4.2 14 3.2Z"/><path d="M3 9v6h4l5 5V4L7 9H3Z"/></svg>' +
        '</button>' +
        '<button type="button" class="reels-close" aria-label="Close">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M6 6l12 12M18 6 6 18"/></svg>' +
        '</button>' +
        '</div>';
      document.body.appendChild(overlay);

      card = overlay.querySelector('.reels-card');
      video = overlay.querySelector('video');
      captionEl = overlay.querySelector('.reels-caption');
      progressWrap = overlay.querySelector('.reels-progress');
      muteBtn = overlay.querySelector('.reels-mute');
      closeBtn = overlay.querySelector('.reels-close');
      prevBtn = overlay.querySelector('.reels-nav--prev');
      nextBtn = overlay.querySelector('.reels-nav--next');

      closeBtn.addEventListener('click', close);
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) close();
      });
      muteBtn.addEventListener('click', function () {
        isMuted = !isMuted;
        video.muted = isMuted;
      });
      prevBtn.addEventListener('click', function () {
        goTo(currentIndex - 1);
      });
      nextBtn.addEventListener('click', function () {
        goTo(currentIndex + 1);
      });
      document.addEventListener('keydown', function (e) {
        if (!isOpen) return;
        if (e.key === 'Escape') close();
        if (e.key === 'ArrowRight') goTo(currentIndex + 1);
        if (e.key === 'ArrowLeft') goTo(currentIndex - 1);
      });
      window.addEventListener('resize', function () {
        if (!isOpen) return;
        positionCard(targetRect());
      });
    }

    function targetRect() {
      var vw = window.innerWidth,
        vh = window.innerHeight;
      var h = Math.min(vh * 0.86, 700);
      var w = Math.min((h * 9) / 16, vw * 0.92, 380);
      h = (w * 16) / 9;
      return { width: w, height: h, top: (vh - h) / 2, left: (vw - w) / 2 };
    }

    function positionCard(r) {
      card.style.top = r.top + 'px';
      card.style.left = r.left + 'px';
      card.style.width = r.width + 'px';
      card.style.height = r.height + 'px';
    }

    function mediaSrc(el) {
      if (!el) return '';
      if (el.tagName === 'VIDEO') {
        var source = el.querySelector('source');
        return el.currentSrc || (source && source.src) || el.getAttribute('src') || '';
      }
      return el.currentSrc || el.src || '';
    }

    function openFrom(itemEl) {
      var row = itemEl.closest('.video-circles-row') || document;
      groupItems = Array.prototype.slice.call(row.querySelectorAll('[data-video-circle]'));
      currentIndex = groupItems.indexOf(itemEl);
      if (currentIndex < 0) currentIndex = 0;

      if (!overlay) buildOverlay();

      var circleEl = itemEl.querySelector('.video-circle');
      var r = circleEl.getBoundingClientRect();
      originRect = r;

      card.style.borderRadius = '50%';
      positionCard(r);
      card.classList.remove('is-expanded');

      progressWrap.innerHTML = '';
      groupItems.forEach(function (_, i) {
        var seg = document.createElement('span');
        seg.className = 'reels-progress__seg' + (i === currentIndex ? ' is-active' : '');
        progressWrap.appendChild(seg);
      });

      loadCurrent();

      isOpen = true;
      overlay.classList.add('is-open');
      requestAnimationFrame(function () {
        overlay.classList.add('is-visible');
        requestAnimationFrame(function () {
          card.style.borderRadius = '20px';
          positionCard(targetRect());
          card.classList.add('is-expanded');
        });
      });
    }

    function loadCurrent() {
      var itemEl = groupItems[currentIndex];
      var media = itemEl.querySelector('.video-circle__media');
      var src = mediaSrc(media);
      captionEl.textContent = itemEl.getAttribute('data-caption') || '';
      if (src) video.src = src;
      video.muted = isMuted;
      video.currentTime = 0;
      var playPromise = video.play();
      if (playPromise && playPromise.catch) playPromise.catch(function () {});

      Array.prototype.forEach.call(progressWrap.children, function (seg, i) {
        seg.classList.toggle('is-active', i === currentIndex);
      });
      prevBtn.disabled = currentIndex === 0;
      nextBtn.disabled = currentIndex === groupItems.length - 1;
    }

    function goTo(index) {
      if (index < 0 || index >= groupItems.length) return;
      currentIndex = index;
      loadCurrent();
    }

    function close() {
      isOpen = false;
      card.classList.remove('is-expanded');
      if (originRect) positionCard(originRect);
      card.style.borderRadius = '50%';
      overlay.classList.remove('is-visible');
      video.pause();
      setTimeout(function () {
        overlay.classList.remove('is-open');
      }, 320);
    }

    document.addEventListener('click', function (e) {
      var item = e.target.closest && e.target.closest('[data-video-circle]');
      if (item) openFrom(item);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var item = e.target.closest && e.target.closest('[data-video-circle]');
      if (item) {
        e.preventDefault();
        openFrom(item);
      }
    });
  });
})();